/*
 * 梦境舞台。
 *
 * 旧产品的核心视觉语言是三明治：梦境背景图 → 角色立绘 → 半透明白色 UI 覆盖层。
 * 这一版把背景和立绘换回旧项目的真实素材（见 public/README.md），
 * CSS 只保留花瓣与光晕这类「会动的空气」，不再自己画天空——
 * 有原画就别用渐变去模仿它。
 *
 * 背景**只有一个铺满视口的元素**，浏览器与手机一视同仁。这个元素可以把
 * 本地兜底图垫在服务端图下面，但不会再复制出一层放大模糊的舞台。
 * 早先是两层（手机列内清晰 + 列外放大模糊当外圈），宽屏上看起来就是
 * 一条清晰竖条配一片灰糊，背景被切成了一条。合并的理由与裁切取舍写在
 * styles.css 的 .backdrop 上。
 */

import { useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

/* -------------------------------------------------------------------------- */
/* 背景                                                                        */
/* -------------------------------------------------------------------------- */

/** 花瓣。9 片，固定参数——随机数会让每次热更新都换一套，调样式时很烦。 */
const PETALS = [
  { x: "6%", delay: "0s", dur: "17s" },
  { x: "18%", delay: "3.4s", dur: "21s" },
  { x: "29%", delay: "8.1s", dur: "15s" },
  { x: "41%", delay: "1.7s", dur: "24s" },
  { x: "54%", delay: "11.2s", dur: "19s" },
  { x: "67%", delay: "5.9s", dur: "22s" },
  { x: "78%", delay: "14.6s", dur: "16s" },
  { x: "88%", delay: "2.8s", dur: "25s" },
  { x: "95%", delay: "9.3s", dur: "18s" },
] as const;

/**
 * 铺满视口的梦境背景：清晰原画 + 飘落花瓣 + 上下压暗。
 *
 * 三样都在视口层而不是内容列里。压暗尤其不能放进内容列——那会在宽屏上
 * 沿着列边留下一条明显的明暗接缝。
 */
/**
 * CSS 多重背景会同时请求两张图：主图成功后盖住兜底图，主图尚未完成或失败时
 * 兜底图仍然可见。不能只在 React 里用 `url ?? fallbackUrl`——那只能判断字符串
 * 是否存在，判断不了这个 URL 对应的图片有没有真的加载出来。
 */
export function DreamBackdrop({
  url,
  fallbackUrl,
}: {
  url: string;
  fallbackUrl?: string;
}) {
  const sources =
    fallbackUrl && fallbackUrl !== url ? [url, fallbackUrl] : [url];
  const backgroundImage = sources
    .map((source) => `url(${JSON.stringify(source)})`)
    .join(", ");

  return (
    <>
      <div
        className="backdrop"
        style={{ backgroundImage }}
        aria-hidden
      />
      <div className="scenery-petals" aria-hidden>
        {PETALS.map((petal) => (
          <span
            key={petal.x}
            className="petal"
            style={
              {
                "--x": petal.x,
                "--delay": petal.delay,
                "--dur": petal.dur,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
      <div className="scenery-veil" aria-hidden />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 立绘                                                                        */
/* -------------------------------------------------------------------------- */

export type PortraitState =
  | { kind: "ready"; url: string; alt: string; fallbackUrl?: string }
  /** 服务端没给、本地也没有。不画灰框，画一团灵体顶着。 */
  | { kind: "unavailable" };

type PortraitSourceStatus = "loading" | "loaded" | "failed";

interface PortraitLoadState {
  key: string;
  primary: PortraitSourceStatus;
  fallback: PortraitSourceStatus;
}

export function StagePortrait({ state }: { state: PortraitState }) {
  const primaryUrl = state.kind === "ready" ? state.url : null;
  const fallbackUrl =
    state.kind === "ready" && state.fallbackUrl !== state.url
      ? state.fallbackUrl
      : undefined;
  const sourceKey = `${primaryUrl ?? ""}\u0000${fallbackUrl ?? ""}`;
  // 图片事件可能晚于 props 切换到达。旧 sourceKey 的 onLoad/onError 绝不能把
  // 新立绘的状态覆盖掉，否则会出现“新图闪过一下，随后 opacity 又回到 0”。
  const activeSourceKeyRef = useRef(sourceKey);
  useLayoutEffect(() => {
    activeSourceKeyRef.current = sourceKey;
  }, [sourceKey]);
  const [loadState, setLoadState] = useState<PortraitLoadState>({
    key: "",
    primary: "loading",
    fallback: "loading",
  });
  const current =
    loadState.key === sourceKey
      ? loadState
      : {
          key: sourceKey,
          primary: "loading" as const,
          fallback: "loading" as const,
        };

  const record = (
    source: "primary" | "fallback",
    status: PortraitSourceStatus,
  ) => {
    setLoadState((previous) => {
      if (activeSourceKeyRef.current !== sourceKey) return previous;
      const base =
        previous.key === sourceKey
          ? previous
          : {
              key: sourceKey,
              primary: "loading" as const,
              fallback: "loading" as const,
            };
      return { ...base, [source]: status };
    });
  };

  if (
    state.kind === "ready" &&
    current.primary === "failed" &&
    (!fallbackUrl || current.fallback === "failed")
  ) {
    return <WispPortrait />;
  }

  if (state.kind === "ready") {
    // fallback 只在主图尚未完成或已经失败时可见。主图一旦加载成功就立刻卸掉
    // fallback；两张透明人物图同时叠着会变成肉眼可见的“双重立绘”。
    const showFallback =
      fallbackUrl !== undefined &&
      current.primary !== "loaded" &&
      current.fallback !== "failed";
    const showLoadingWisp =
      current.primary === "loading" && !showFallback;

    return (
      <div className={`portrait${showLoadingWisp ? " portrait--wisp" : ""}`}>
        {showLoadingWisp ? (
          <WispContents note="立绘正在显形…" />
        ) : null}

        {showFallback ? (
          <img
            key={`fallback:${sourceKey}`}
            className={`portrait__image portrait__image--fallback${
              current.fallback === "loaded" ? " is-loaded" : ""
            }`}
            src={fallbackUrl}
            alt={current.primary === "failed" ? state.alt : ""}
            aria-hidden={current.primary === "failed" ? undefined : true}
            onLoad={() => record("fallback", "loaded")}
            onError={() => record("fallback", "failed")}
          />
        ) : null}

        {current.primary !== "failed" ? (
          <img
            key={`primary:${sourceKey}`}
            className={`portrait__image portrait__image--primary${
              current.primary === "loaded" ? " is-loaded" : ""
            }`}
            src={state.url}
            alt={state.alt}
            onLoad={() => record("primary", "loaded")}
            onError={() => record("primary", "failed")}
          />
        ) : null}
      </div>
    );
  }

  return <WispPortrait />;
}

function WispPortrait() {
  return (
    <div className="portrait portrait--wisp">
      <WispContents note="立绘随角色数据下发，当前读取不到" />
    </div>
  );
}

function WispContents({ note }: { note: string }) {
  return (
    <>
      <div className="wisp" aria-hidden>
        <span className="wisp__halo wisp__halo--outer" />
        <span className="wisp__halo wisp__halo--inner" />
        <span className="wisp__core" />
        <span className="wisp__mote wisp__mote--a" />
        <span className="wisp__mote wisp__mote--b" />
        <span className="wisp__mote wisp__mote--c" />
      </div>
      <p className="portrait__note">{note}</p>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 舞台容器                                                                    */
/* -------------------------------------------------------------------------- */

export function Stage({
  hud,
  rail,
  portrait,
  children,
}: {
  hud: ReactNode;
  rail: ReactNode;
  portrait: ReactNode;
  children: ReactNode;
}) {
  /*
   * 把左上数值面板的实测高度发布成 `--hud-h`。
   *
   * 气泡模式的消息区上沿要正好落在「面板下沿 + 一个 --gutter」处，
   * 而面板高度是内容决定的：三行数值、好感称号可能换行、字体加载完还会重排。
   * 写死一个数迟早对不上，所以量出来。
   *
   * 面板整块不渲染时是 0——那时消息区顶到 safe-top + gutter，正确。
   */
  const hudRef = useRef<HTMLDivElement>(null);
  const [hudHeight, setHudHeight] = useState(0);

  useLayoutEffect(() => {
    const el = hudRef.current;
    if (!el) {
      setHudHeight(0);
      return;
    }
    const measure = () => setHudHeight(el.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // hud 由有无数值决定渲染与否，它一变就要重新接观察者。
  }, [hud]);

  return (
    <div
      className="stage"
      style={{ "--hud-h": `${hudHeight}px` } as CSSProperties}
    >
      <div className="stage__figure">{portrait}</div>
      {/* hud 与 rail 都可能整块不渲染（没有任何数值 / 取不到用户信息）。
          外壳 div 必须跟着一起消失，否则留下一个有内边距的空盒子，
          把下面的内容顶下去一截。 */}
      {hud ? (
        <div className="stage__hud" ref={hudRef}>
          {hud}
        </div>
      ) : null}
      {rail}
      <div className="stage__content">{children}</div>
    </div>
  );
}
